import {
  Artboard,
  CanvasRenderer,
  Renderer,
  RiveCanvas as RiveCanvasType,
} from '@rive-app/canvas-advanced';
import { isEmpty, map } from 'lodash';

import { Animation } from '@repo/database-core';
import { CharacterEngineOptions, LoadedRiveAsset } from '@repo/types-client';

import RiveFileLoader from './rive-file-loader';
import RuntimeLoader from './runtime-loader';

export default class CharacterEngine {
  private lastTime = 0;
  private currentSkin = 0;
  private currentHairColor = 0;
  private currentHairPinch = 0;
  private canvas: HTMLCanvasElement | null;
  private rive: RiveCanvasType | null = null;
  private renderer: CanvasRenderer | Renderer | null = null;
  private baseAnimations: Animation[] = [];
  private equippedAnimations: Animation[] = [];
  private animationIdsToHide: string[] = [];
  private fileLoader!: RiveFileLoader;
  public initialized = false;
  private frameId?: number;

  constructor({ canvas }: CharacterEngineOptions) {
    this.canvas = canvas;
  }

  async init() {
    this.rive = (await RuntimeLoader.awaitInstance()) as RiveCanvasType;
    if (this.canvas) {
      this.renderer = this.rive.makeRenderer(this.canvas);
    }
    this.fileLoader = new RiveFileLoader({
      rive: this.rive,
    });
    this.initialized = true;
  }

  reset() {
    if (!this.rive) return;

    if (this.frameId !== undefined) {
      this.rive.cancelAnimationFrame(this.frameId);
      this.frameId = undefined;
    }

    // Cleanup resources
    if (this.renderer) this.renderer.delete();

    this.baseAnimations = [];
    this.equippedAnimations = [];
    this.animationIdsToHide = [];

    this.canvas = null;
    this.rive = null;
  }

  async setBaseAnimations(newAnimations: Animation[]) {
    if (!this.rive || newAnimations.length === 0) return;

    const loadedIds = map(this.fileLoader?.loadedLayers, 'id');
    if (loadedIds.includes(newAnimations[0].id)) return;

    this.baseAnimations = newAnimations;

    await this.fileLoader?.loadAnimations(newAnimations);
    this.updateEquippedAnimations(newAnimations);
  }

  async setEquippedAnimations(newAnimations: Animation[]) {
    await this.fileLoader?.loadAnimations(newAnimations);
    this.updateEquippedAnimations(newAnimations);
  }

  setAnimationsToHide(animationsToHide: Animation[]) {
    this.animationIdsToHide = animationsToHide.map((a) => a.id);
  }

  updateSkin(currentSkin: number) {
    this.currentSkin = currentSkin;
  }

  updateHairColor(currentHairColor: number) {
    this.currentHairColor = currentHairColor;
  }

  updateHairPinch(currentHairPinch: number) {
    this.currentHairPinch = currentHairPinch;
  }

  updateEquippedAnimations(equippedAnimations: Animation[]) {
    this.equippedAnimations = [
      ...this.equippedAnimations,
      ...equippedAnimations,
    ];
    this.fileLoader?.sortLayers();
  }

  animateAsset({
    asset,
    currentSkin,
    currentHairColor,
    currentHairPinch,
    elapsedTimeSec,
    isVisible = true,
  }: {
    asset: LoadedRiveAsset;
    currentSkin: number;
    currentHairColor: number;
    currentHairPinch: number;
    elapsedTimeSec: number;
    isVisible?: boolean;
  }) {
    if (!isVisible) return;

    const { stateMachine, artboard } = asset;
    stateMachine.advance(elapsedTimeSec);

    // Update skin tone
    if (asset.type === 'BODY' && stateMachine.input(1)) {
      const input = stateMachine.input(1).asNumber();
      input.value = currentSkin;
    }

    // Update hair color and pinch
    if (asset.type === 'HAIR') {
      const hairColorInput = stateMachine.input(1).asNumber();
      hairColorInput.value = currentHairColor;

      const hairPinchInput = stateMachine.input(2).asNumber();
      hairPinchInput.value = currentHairPinch;
    }

    // Advance artboard and draw it
    artboard.advance(elapsedTimeSec);
    this.alignArtboard(artboard, isVisible);
  }

  alignArtboard(artboard: Artboard, isVisible: boolean) {
    if (!this.renderer || !this.canvas || !this.rive || !artboard) return;

    this.renderer.save();
    this.renderer.align(
      this.rive.Fit.contain,
      this.rive.Alignment.center,
      {
        minX: 0,
        minY: 0,
        maxX: this.canvas.width,
        maxY: this.canvas.height,
      },
      artboard.bounds,
    );
    if (isVisible) {
      artboard.draw(this.renderer);
    }
    this.renderer.restore();
  }

  isVisible(equippedAnimationIds: string[], layerId: string) {
    return (
      equippedAnimationIds.includes(layerId) &&
      !this.animationIdsToHide.includes(layerId)
    );
  }

  async run() {
    if (!this.rive) return;

    const renderLoop = (time: number) => {
      if (!this.renderer || !this.rive) return;

      const equippedAnimationIds = isEmpty(this.equippedAnimations)
        ? map(this.baseAnimations, 'id')
        : map([...this.equippedAnimations, ...this.baseAnimations], 'id');

      if (!this.lastTime) {
        this.lastTime = time;
      }
      const elapsedTimeMs = time - this.lastTime;
      const elapsedTimeSec = elapsedTimeMs / 1000;
      this.lastTime = time;

      // Clear previous frame
      this.renderer.clear();

      // Animate layers
      this.fileLoader.sortedLayers.forEach((layer) => {
        const isVisible = this.isVisible(equippedAnimationIds, layer.id);
        this.animateAsset({
          asset: layer,
          currentSkin: this.currentSkin,
          currentHairColor: this.currentHairColor,
          currentHairPinch: this.currentHairPinch,
          elapsedTimeSec,
          isVisible,
        });
      });

      // Request next frame
      this.frameId = this.rive.requestAnimationFrame(renderLoop);
    };

    this.frameId = this.rive.requestAnimationFrame(renderLoop);
  }
}
